/* ==========================================================================
   PiHOT drawers — entrance choreography for .p-drawer panels (cart, search).
   The panel slide itself is CSS (assets/pihot-drawer.css); this staggers the
   content in once the panel starts moving, grows the free-shipping bar and
   handles a few cart behaviours the Vision markup used to own.
   ========================================================================== */

(() => {
  const ITEM_SELECTOR = [
    '[data-p-drawer-item]',
    '.p-search .side-panel-content--initial .thb-predictive-search > *',
    '#Product-Drawer .product-gallery-container',
    '#Product-Drawer .product-information--inner > *',
    '#Product-Drawer .p-pdp__media',
    '#Product-Drawer .p-pdp__info > *',
    '.p-popup__body > *',
  ].join(',');

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const openPanels = new WeakSet();
  const contexts = new WeakMap();

  const isDrawer = (panel) =>
    panel instanceof HTMLElement && (panel.classList.contains('p-drawer') || panel.id === 'Product-Drawer');

  const animateIn = (panel) => {
    const { gsap } = window;
    if (!gsap || reduceMotion.matches) return;

    contexts.get(panel)?.revert();
    contexts.set(
      panel,
      gsap.context(() => {
        const items = gsap.utils.toArray(ITEM_SELECTOR, panel).filter((el) => el.getClientRects().length);
        gsap.from(items, {
          opacity: 0,
          y: 16,
          duration: 0.8,
          ease: 'power3.out',
          stagger: items.length > 8 ? { amount: 0.4 } : 0.05,
          delay: 0.12,
          clearProps: 'opacity,transform',
        });

        const media = panel.querySelector('[data-p-popup-media] img');
        if (media) {
          gsap.from(media, { scale: 1.04, duration: 1.2, ease: 'expo.out', clearProps: 'transform' });
        }

        const fill = panel.querySelector('.p-cart__shipping-fill');
        if (fill) {
          gsap.from(fill, {
            scaleX: 0,
            transformOrigin: 'left center',
            duration: 0.8,
            ease: 'power3.out',
            delay: 0.35,
            clearProps: 'transform',
          });
        }
      }, panel)
    );
  };

  /* Width of a classic (non-overlay) scrollbar, read by .p-drawer__body padding.
     Set on the panel itself, which partial cart refreshes never rewrite. Browsers
     without scrollbar-gutter (Safari) only show the bar once content overflows, e.g.
     after recommendations load, so the scroller is watched: the bar appearing or
     going away resizes its content box. */
  const setScrollbar = (panel, scroller) => {
    panel.style.setProperty('--p-drawer-scrollbar', `${scroller.offsetWidth - scroller.clientWidth}px`);
  };

  const scrollbarObserver =
    'ResizeObserver' in window &&
    new ResizeObserver((entries) => {
      entries.forEach(({ target }) => {
        const panel = target.closest('.p-drawer, #Product-Drawer');
        if (panel) setScrollbar(panel, target);
      });
    });

  const fitScrollbar = (panel) => {
    const scroller = panel.querySelector('.p-drawer__body, #Product-Drawer-Content') || panel;
    setScrollbar(panel, scroller);
    if (scrollbarObserver) scrollbarObserver.observe(scroller);
  };

  window.addEventListener('resize', () => {
    document.querySelectorAll('.p-drawer.active, #Product-Drawer.active').forEach(fitScrollbar);
  });

  /* The PiHOT quick view brings its own stylesheet; hold the entrance until it applies. */
  const whenStyled = (panel) =>
    Promise.all(
      [...panel.querySelectorAll('link[rel="stylesheet"]')]
        .filter((link) => !link.sheet)
        .map((link) => new Promise((resolve) => {
          link.addEventListener('load', resolve, { once: true });
          link.addEventListener('error', resolve, { once: true });
        }))
    );

  document.addEventListener('panel:open', (event) => {
    const panel = event.detail?.panel;
    if (!isDrawer(panel)) return;
    fitScrollbar(panel);
    if (openPanels.has(panel)) return;
    openPanels.add(panel);
    if (panel.id === 'Product-Drawer') {
      whenStyled(panel).then(() => {
        if (!openPanels.has(panel)) return;
        fitScrollbar(panel);
        animateIn(panel);
      });
    } else {
      animateIn(panel);
    }
    if (panel.classList.contains('p-cart')) {
      requestAnimationFrame(() => panel.querySelector('.p-drawer__close-btn')?.focus({ preventScroll: true }));
    } else if (panel.id === 'Product-Drawer') {
      requestAnimationFrame(() => panel.focus({ preventScroll: true }));
    }
  });

  document.addEventListener('panel:closed', (event) => {
    const panel = event.detail?.panel;
    if (!isDrawer(panel)) return;
    openPanels.delete(panel);
    contexts.get(panel)?.revert();
    contexts.delete(panel);
  });

  /* Pop-up (modal-dialog.p-popup): Vision toggles [open]; the panel drop is CSS.
     Vision only remembers a dismissal made with the close button, so any close
     (scrim, Esc, "No thanks") and any newsletter signup are stored under the same
     key (the element id) to keep the pop-up from returning on every page. */
  const POPUP_POSTED = 'p-popup-posted';
  const designMode = () => Boolean(window.Shopify?.designMode);

  const rememberPopup = (popup) => {
    if (designMode()) return;
    try {
      window.localStorage.setItem(popup.id, JSON.stringify(new Date()));
    } catch (error) {
      /* Storage unavailable (private mode): the pop-up simply shows again. */
    }
  };

  new MutationObserver((records) => {
    records.forEach(({ target }) => {
      if (!target.matches?.('modal-dialog.p-popup')) return;
      if (target.hasAttribute('open')) {
        animateIn(target);
      } else {
        contexts.get(target)?.revert();
        contexts.delete(target);
        rememberPopup(target);
      }
    });
  }).observe(document.body, { attributes: true, attributeFilter: ['open'], subtree: true });

  document.addEventListener('click', (event) => {
    if (!event.target.closest('[data-p-popup-dismiss]')) return;
    event.target.closest('modal-dialog')?.hide();
  });

  document.addEventListener('submit', (event) => {
    if (event.target.id !== 'PPopupNewsletter') return;
    try {
      window.sessionStorage.setItem(POPUP_POSTED, '1');
    } catch (error) {
      /* Without storage the result shows in the pop-up's markup only. */
    }
  });

  const initPopup = () => {
    const popup = document.querySelector('modal-dialog.p-popup');
    if (!popup || designMode()) return;
    let posted = false;
    try {
      posted = window.sessionStorage.getItem(POPUP_POSTED) === '1';
      window.sessionStorage.removeItem(POPUP_POSTED);
    } catch (error) {
      posted = false;
    }
    const success = popup.querySelector('[data-p-popup-success]');
    if (success) rememberPopup(popup);
    if (posted && (success || popup.querySelector('[data-p-popup-error]'))) {
      customElements.whenDefined('modal-dialog').then(() => popup.show());
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPopup, { once: true });
  } else {
    initPopup();
  }

  /* Search drawer: suggestion chips run the predictive search in place, recent
     searches live in localStorage, and the field gets its own clear button. */
  const searchDrawer = document.getElementById('Search-Drawer');

  if (searchDrawer) {
    const RECENT_KEY = 'p-recent-searches';
    const RECENT_MAX = 6;
    const searchForm = searchDrawer.querySelector('search-form');
    const input = searchDrawer.querySelector('.search-field');
    const reset = searchDrawer.querySelector('[data-p-search-reset]');
    const recent = searchDrawer.querySelector('[data-p-search-recent]');
    const recentList = recent?.querySelector('[data-p-search-recent-list]');
    const recentIcon = recent?.querySelector('[data-p-search-recent-icon]');

    const readRecent = () => {
      try {
        const list = JSON.parse(window.localStorage.getItem(RECENT_KEY));
        return Array.isArray(list) ? list.filter((term) => typeof term === 'string' && term) : [];
      } catch (error) {
        return [];
      }
    };

    const writeRecent = (list) => {
      try {
        if (list.length) window.localStorage.setItem(RECENT_KEY, JSON.stringify(list));
        else window.localStorage.removeItem(RECENT_KEY);
      } catch (error) {
        /* Storage unavailable: recent searches are simply not kept. */
      }
    };

    const renderRecent = () => {
      if (!recentList) return;
      const list = readRecent();
      recent.hidden = !list.length;
      recentList.replaceChildren(
        ...list.map((term) => {
          const item = document.createElement('li');
          const chip = document.createElement('a');
          chip.className = 'p-search__chip';
          chip.href = `${window.theme?.routes?.search_url || '/search'}?q=${encodeURIComponent(term)}`;
          chip.dataset.pSearchTerm = term;
          if (recentIcon) chip.append(recentIcon.content.cloneNode(true));
          chip.append(term);
          item.append(chip);
          return item;
        })
      );
    };

    const saveRecent = (value) => {
      if (!recentList) return;
      const term = value.trim().replace(/\s+/g, ' ').slice(0, 60);
      if (!term) return;
      const key = term.toLowerCase();
      writeRecent([term, ...readRecent().filter((item) => item.toLowerCase() !== key)].slice(0, RECENT_MAX));
      renderRecent();
    };

    const syncReset = () => {
      if (reset) reset.hidden = !input.value;
    };

    const runSearch = (term) => {
      input.value = term;
      syncReset();
      saveRecent(term);
      if (!searchForm?.predictiveSearchResults) {
        searchForm?.querySelector('form')?.submit();
        return;
      }
      searchForm.onChange();
      /* The chip is hidden with the default content: keep focus in the drawer
         without raising the on-screen keyboard over the results on touch. */
      if (window.matchMedia('(pointer: fine)').matches) {
        input.focus({ preventScroll: true });
      } else {
        input.blur();
        searchDrawer.focus({ preventScroll: true });
      }
    };

    searchDrawer.addEventListener('click', (event) => {
      const chip = event.target.closest('[data-p-search-term]');
      if (chip && !(event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)) {
        event.preventDefault();
        runSearch(chip.dataset.pSearchTerm);
        return;
      }
      if (event.target.closest('[data-p-search-reset]')) {
        input.value = '';
        syncReset();
        searchForm?.onChange?.();
        input.focus({ preventScroll: true });
        return;
      }
      if (event.target.closest('[data-p-search-clear-recent]')) {
        writeRecent([]);
        renderRecent();
        input.focus({ preventScroll: true });
        return;
      }
      if (event.target.closest('.side-panel-content--has-tabs a, #search-results-submit')) saveRecent(input.value);
    });

    searchDrawer.addEventListener('submit', () => saveRecent(input.value));
    input.addEventListener('input', syncReset);
    document.addEventListener('panel:open', (event) => {
      if (event.detail?.panel === searchDrawer) renderRecent();
    });
    renderRecent();
    syncReset();
  }

  /* Search page field (snippets/pihot-main-search.liquid): same clear button. */
  const pageSearch = document.getElementById('PSearchPageInput');
  const pageReset = document.querySelector('[data-p-search-page-reset]');

  if (pageSearch && pageReset) {
    pageSearch.addEventListener('input', () => {
      pageReset.hidden = !pageSearch.value;
    });
    pageReset.addEventListener('click', () => {
      pageSearch.value = '';
      pageReset.hidden = true;
      pageSearch.focus();
    });
  }

  /* Cart drawer and cart page (#Cart.p-cart) share the markup handled below. */
  const TERMS = 'terms-checkbox input[type="checkbox"]';

  document.addEventListener('click', (event) => {
    const checkout = event.target.closest('.p-cart__checkout');
    if (!checkout) return;
    const terms = checkout.closest('.p-cart')?.querySelector(TERMS);
    if (terms && !terms.checked) {
      event.preventDefault();
      terms.setCustomValidity(window.theme?.strings?.requiresTerms || '');
      terms.reportValidity();
      terms.focus();
    }
  });

  document.addEventListener('change', (event) => {
    if (event.target.matches(`.p-cart ${TERMS}`)) event.target.setCustomValidity('');
    if (event.target.matches('.p-cart__note')) saveNote(event.target);
  });

  /* Discount code / order note toggles. The cart markup is re-rendered after
     every cart change, so the panel the shopper opened is re-applied afterwards.
     No entry means "use the server default" (discount open when applied). */
  const openExtras = new WeakMap();

  const setExtra = (cart, id) => {
    cart.querySelectorAll('.p-cart__toggle').forEach((toggle) => {
      const target = toggle.getAttribute('aria-controls');
      const panel = cart.querySelector(`#${target}`);
      toggle.setAttribute('aria-expanded', String(target === id));
      if (panel) panel.hidden = target !== id;
    });
  };

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('.p-cart__toggle');
    if (!toggle) return;
    const cart = toggle.closest('.p-cart');
    const id = toggle.getAttribute('aria-expanded') === 'true' ? null : toggle.getAttribute('aria-controls');
    openExtras.set(cart, id);
    setExtra(cart, id);
    if (id) cart.querySelector(`#${id}`)?.querySelector('input, textarea')?.focus({ preventScroll: true });
  });

  /* Partial refresh. Every Vision cart update (quick add, quantity, remove,
     discount, cart:refresh) writes the whole section into the drawer's and the
     cart page's innerHTML. Intercept those writes and morph the live DOM towards
     the new markup instead, so only nodes whose content changed are touched;
     images, loaded recommendations and open panels stay put. */
  const cartDrawer = document.getElementById('Cart-Drawer');
  const cartPage = document.querySelector('#Cart.p-cart');
  const innerHTML = Object.getOwnPropertyDescriptor(Element.prototype, 'innerHTML');
  let morphRoot;

  /* Custom elements whose state is computed in connectedCallback: morphed in
     place, then re-synced. Others are kept when unchanged, replaced otherwise. */
  const RESYNC = {
    'FREE-SHIPPING': (el) => el.connectedCallback?.(),
    'QUANTITY-SELECTOR': (el) => {
      el.classList.add('buttons_added');
      el.validateQtyRules?.();
    },
  };

  /* Server markup of kept custom elements, compared instead of their live
     outerHTML, which Vision scripts mutate at runtime. */
  const SOURCE = Symbol('pihotSource');
  const snapshot = (root) => {
    if (root.nodeType !== 1) return;
    [root, ...root.querySelectorAll('*')].forEach((el) => {
      if (el.tagName.includes('-')) el[SOURCE] = el.outerHTML;
    });
  };

  const keyOf = (node) => (node.nodeType === 1 ? node.getAttribute('data-p-key') || node.id || '' : '');

  const keepAttr = (el, name) =>
    openExtras.has(morphRoot) &&
    ((name === 'hidden' && el.classList.contains('p-cart__panel')) ||
      (name === 'aria-expanded' && el.classList.contains('p-cart__toggle')));

  const syncAttrs = (from, to) => {
    for (const { name } of [...from.attributes]) {
      if (!to.hasAttribute(name) && !keepAttr(from, name)) from.removeAttribute(name);
    }
    for (const { name, value } of [...to.attributes]) {
      if (from.getAttribute(name) !== value && !keepAttr(from, name)) from.setAttribute(name, value);
    }
  };

  const swapRecommendations = (from, to) => {
    if (from.dataset.url === to.dataset.url) return;
    to.hidden = true;
    from.after(to);
    const swap = () => {
      observer.disconnect();
      clearTimeout(timer);
      if (from.isConnected) from.remove();
      to.hidden = false;
    };
    const observer = new MutationObserver(() => {
      if (to.classList.contains('product-recommendations--loaded')) swap();
    });
    observer.observe(to, { attributes: true, attributeFilter: ['class'] });
    const timer = setTimeout(swap, 5000);
  };

  const revealInserted = (node) => {
    const { gsap } = window;
    if (!gsap || reduceMotion.matches || node.nodeType !== 1) return;
    const inDrawer = morphRoot === cartDrawer;
    if (inDrawer && !(cartDrawer.classList.contains('active') && node.matches('[data-p-drawer-item]'))) return;
    if (!inDrawer && !node.matches('.p-cart-item')) return;
    gsap.from(node, { opacity: 0, y: 8, duration: 0.4, ease: 'power2.out', clearProps: 'opacity,transform' });
  };

  const patch = (from, to) => {
    if (from.nodeType !== 1) {
      if (from.nodeValue !== to.nodeValue) from.nodeValue = to.nodeValue;
      return;
    }
    const tag = from.tagName;
    if (tag === 'PRODUCT-RECOMMENDATIONS') return swapRecommendations(from, to);
    if (tag.includes('-') && !RESYNC[tag]) {
      const html = to.outerHTML;
      const previous = from[SOURCE] ?? from.outerHTML.replace(/ style=""/g, '');
      if (previous !== html) {
        snapshot(to);
        from.replaceWith(to);
      } else {
        from[SOURCE] = html;
      }
      return;
    }
    syncAttrs(from, to);
    if (tag === 'INPUT') {
      if (from.value !== to.value) from.value = to.value;
      if (from.checked !== to.checked) from.checked = to.checked;
    } else if (tag === 'TEXTAREA') {
      if (from !== document.activeElement && from.value !== to.value) from.value = to.value;
      return;
    }
    morphChildren(from, to);
    RESYNC[tag]?.(from);
  };

  const sameKind = (a, b) => a.nodeType === b.nodeType && a.nodeName === b.nodeName;

  /* Nodes are never moved: moving re-runs connectedCallback, which in Vision
     elements re-binds listeners. Unmatched old nodes are removed instead. */
  function morphChildren(from, to) {
    let cur = from.firstChild;
    for (const next of [...to.childNodes]) {
      const key = keyOf(next);
      let match = null;
      if (key) {
        for (let n = cur; n; n = n.nextSibling) {
          if (keyOf(n) === key && sameKind(n, next)) {
            match = n;
            break;
          }
        }
      } else if (cur && !keyOf(cur) && sameKind(cur, next)) {
        match = cur;
      }

      if (match) {
        while (cur !== match) {
          const gone = cur;
          cur = cur.nextSibling;
          gone.remove();
        }
        cur = match.nextSibling;
        patch(match, next);
      } else {
        snapshot(next);
        from.insertBefore(next, cur);
        revealInserted(next);
      }
    }
    while (cur) {
      const gone = cur;
      cur = cur.nextSibling;
      gone.remove();
    }
  }

  /* A kept recommendations block may list a product that was just added. */
  const hideRecommendedInCart = () => {
    const inCart = new Set([...cartDrawer.querySelectorAll('.p-cart-item[data-p-product]')].map((li) => li.dataset.pProduct));
    cartDrawer.querySelectorAll('.p-cart__reco-slide[data-p-product]').forEach((li) => {
      li.hidden = inCart.has(li.dataset.pProduct);
    });
    cartDrawer.querySelectorAll('p-cart-carousel').forEach((el) => el.refresh?.());
  };

  /* Cart page: the recommendations section below lists products picked before the
     latest change; hide the ones now in the bag, show them again once removed. */
  const syncPageRecommendations = () => {
    const inCart = new Set([...cartPage.querySelectorAll('.p-cart-item[data-p-product]')].map((li) => li.dataset.pProduct));
    document.querySelectorAll('.p-recs .p-fc__item[data-p-product]').forEach((li) => {
      li.hidden = inCart.has(li.dataset.pProduct);
    });
  };

  const afterRender = (root) => {
    if (openExtras.has(root)) setExtra(root, openExtras.get(root));
    if (root === cartDrawer) hideRecommendedInCart();
    if (root === cartPage) syncPageRecommendations();
  };

  /* `marker` must exist in both the live and the new markup to morph; otherwise
     (e.g. the cart just became empty) the write goes through as is. */
  const interceptRender = (root, marker) => {
    Object.defineProperty(root, 'innerHTML', {
      configurable: true,
      get() {
        return innerHTML.get.call(this);
      },
      set(html) {
        const template = document.createElement('template');
        template.innerHTML = html;
        if (this.querySelector(marker) && template.content.querySelector(marker)) {
          morphRoot = this;
          try {
            morphChildren(this, template.content);
          } catch (error) {
            console.error(error);
            innerHTML.set.call(this, html);
          } finally {
            morphRoot = undefined;
          }
        } else {
          innerHTML.set.call(this, html);
        }
        afterRender(this);
      },
    });
  };

  if (cartDrawer && innerHTML) {
    interceptRender(cartDrawer, '.p-drawer__inner');
    document.addEventListener('panel:closed', (event) => {
      if (event.detail?.panel === cartDrawer) openExtras.delete(cartDrawer);
    });
  }

  if (cartPage && innerHTML) interceptRender(cartPage, '.p-cart__items');

  /* The cart page already shows the bag: adding from its recommendations, or the
     header bag button, brings the list into view instead of opening the drawer. */
  if (cartPage && cartDrawer) {
    cartDrawer.open = () => {
      const { top } = cartPage.getBoundingClientRect();
      if (top >= 0 && top < window.innerHeight / 2) return;
      cartPage.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'start' });
    };
  }

  /* Order note: saved as the shopper types (debounced) and on blur. */
  let noteTimer;
  let savedNote;

  const saveNote = (textarea) => {
    clearTimeout(noteTimer);
    const note = textarea.value;
    if (note === savedNote) return;
    savedNote = note;

    const request = fetch(`${window.theme?.routes?.cart_update_url || '/cart/update'}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ note }),
    }).then((response) => response.json());

    if (typeof window.publishStandardEvent === 'function') {
      window.publishStandardEvent('CartNoteUpdateEvent', {
        context: textarea.closest('cart-drawer') ? 'dialog' : 'cart',
        note,
        promise: request
          .then((cart) => ({ cart: window.standardEventCartFromAjax?.(cart) ?? null }))
          .catch(() => ({ cart: null })),
      });
    }

    request
      .then(() => {
        const drawer = textarea.closest('.p-cart');
        const status = textarea.parentElement.querySelector('.p-cart__note-status');
        const dot = drawer?.querySelector('.p-cart__toggle-dot');
        if (status) status.textContent = status.dataset.saved || '';
        if (dot) dot.hidden = !note.trim();
      })
      .catch(() => {
        savedNote = undefined;
      });
  };

  /* Back to top is anchored to the Inbox launcher (pihot-drawer.css). An anchor
     only resolves when it precedes the positioned box in tree order, and Inbox
     appends <shopify-chat> to the body late, so move the button after it. */
  if (CSS.supports('anchor-name: --p-a')) {
    const placeBackToTop = () => {
      const chat = document.querySelector('body > shopify-chat');
      const button = document.querySelector('back-to-top');
      if (!chat || !button) return false;
      if (!(chat.compareDocumentPosition(button) & Node.DOCUMENT_POSITION_FOLLOWING)) chat.after(button);
      return true;
    };

    if (!placeBackToTop()) {
      const observer = new MutationObserver(() => {
        if (placeBackToTop()) observer.disconnect();
      });
      observer.observe(document.body, { childList: true });
      window.addEventListener('load', () => setTimeout(() => observer.disconnect(), 10000), { once: true });
    }
  }

  document.addEventListener('input', (event) => {
    if (!event.target.matches('.p-cart__note')) return;
    const textarea = event.target;
    const status = textarea.parentElement.querySelector('.p-cart__note-status');
    if (status) status.textContent = '';
    clearTimeout(noteTimer);
    noteTimer = setTimeout(() => saveNote(textarea), 800);
  });

  /* Cart recommendation carousel: one card at a time, arrows + dots + swipe. */
  if (!customElements.get('p-cart-carousel')) {
    class PCartCarousel extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-p-reco-track]');
        this.dotsEl = this.querySelector('[data-p-reco-dots]');
        this.prevBtn = this.querySelector('[data-p-reco-prev]');
        this.nextBtn = this.querySelector('[data-p-reco-next]');
        if (!this.track) return;

        this.onScroll = () => this.sync();
        this.track.addEventListener('scroll', this.onScroll, { passive: true });
        this.prevBtn?.addEventListener('click', () => this.go(-1));
        this.nextBtn?.addEventListener('click', () => this.go(1));
        this.refresh();
      }

      disconnectedCallback() {
        this.track?.removeEventListener('scroll', this.onScroll);
      }

      slides() {
        return [...this.track.querySelectorAll('[data-p-reco-slide]')].filter((el) => !el.hidden);
      }

      refresh() {
        if (!this.track) return;
        const slides = this.slides();
        const multi = slides.length > 1;
        this.toggleAttribute('data-single', !multi);
        if (this.prevBtn) this.prevBtn.hidden = !multi;
        if (this.nextBtn) this.nextBtn.hidden = !multi;
        if (this.dotsEl) {
          this.dotsEl.hidden = !multi;
          if (multi) this.buildDots(slides.length);
          else this.dotsEl.replaceChildren();
        }
        const nav = this.querySelector('.p-cart__reco-nav');
        if (nav) nav.hidden = !multi;
        this.sync();
      }

      buildDots(count) {
        if (!this.dotsEl || this.dotsEl.childElementCount === count) return;
        const frag = document.createDocumentFragment();
        for (let i = 0; i < count; i += 1) {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'p-cart__reco-dot';
          btn.setAttribute('role', 'tab');
          btn.setAttribute('aria-label', `${i + 1}`);
          btn.addEventListener('click', () => this.goTo(i));
          frag.append(btn);
        }
        this.dotsEl.replaceChildren(frag);
      }

      index() {
        const slides = this.slides();
        if (!slides.length) return 0;
        const left = this.track.scrollLeft;
        let best = 0;
        let bestDist = Infinity;
        slides.forEach((slide, i) => {
          const dist = Math.abs(slide.offsetLeft - left);
          if (dist < bestDist) {
            bestDist = dist;
            best = i;
          }
        });
        return best;
      }

      sync() {
        const slides = this.slides();
        const i = this.index();
        this.prevBtn && (this.prevBtn.disabled = i <= 0);
        this.nextBtn && (this.nextBtn.disabled = i >= slides.length - 1);
        this.dotsEl?.querySelectorAll('.p-cart__reco-dot').forEach((dot, n) => {
          dot.setAttribute('aria-selected', n === i ? 'true' : 'false');
        });
      }

      go(delta) {
        this.goTo(this.index() + delta);
      }

      goTo(i) {
        const slides = this.slides();
        const slide = slides[Math.max(0, Math.min(i, slides.length - 1))];
        if (!slide) return;
        this.track.scrollTo({ left: slide.offsetLeft, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      }
    }
    customElements.define('p-cart-carousel', PCartCarousel);
  }
})();
